import type { GraphData, GraphEdge, EdgeType } from "../types/graph";
import type { FilterState } from "../types/filters";

/**
 * Cross-service edge types used for the crossServiceOnly filter.
 */
const CROSS_SERVICE_EDGE_TYPES: ReadonlySet<EdgeType> = new Set([
  "HTTP_CALLS",
  "CROSS_CALLS",
  "EMITS",
  "LISTENS_ON",
]);

/**
 * Filters graph data based on the active filter state.
 *
 * Pure function — returns a new GraphData without mutating the input.
 *
 * Filtering logic:
 * 1. Nodes are filtered by language, kind, and communityId (empty filter = no restriction).
 * 2. If crossServiceOnly is true, only nodes connected to at least one cross-service edge are kept.
 * 3. Edges are filtered by type (empty filter = no restriction) and both endpoints must be visible.
 * 4. If crossServiceOnly is true, only cross-service edge types are kept.
 */
export function filterGraph(data: GraphData, filters: FilterState): GraphData {
  const { languages, kinds, communityIds, edgeTypes, crossServiceOnly } = filters;

  // Determine which edges are cross-service for the crossServiceOnly node filter
  const crossServiceNodeIds = crossServiceOnly
    ? getCrossServiceNodeIds(data.edges)
    : null;

  // Step 1: Filter nodes
  const filteredNodes = data.nodes.filter((node) => {
    // Language filter
    if (languages.length > 0 && !languages.includes(node.language ?? "")) {
      return false;
    }

    // Kind filter
    if (kinds.length > 0 && !kinds.includes(node.kind)) {
      return false;
    }

    // Community ID filter
    if (communityIds.length > 0 && !communityIds.includes(node.community_id ?? -1)) {
      return false;
    }

    // Cross-service only: node must have at least one cross-service edge
    if (crossServiceOnly && crossServiceNodeIds && !crossServiceNodeIds.has(node.id)) {
      return false;
    }

    return true;
  });

  // Build a set of visible node IDs for edge filtering
  const visibleNodeIds = new Set(filteredNodes.map((n) => n.id));

  // Step 2: Filter edges
  const filteredEdges = data.edges.filter((edge) => {
    // Edge type filter
    if (edgeTypes.length > 0 && !edgeTypes.includes(edge.type)) {
      return false;
    }

    // Cross-service only: only keep cross-service edge types
    if (crossServiceOnly && !CROSS_SERVICE_EDGE_TYPES.has(edge.type)) {
      return false;
    }

    // Both endpoints must be in the visible node set
    if (!visibleNodeIds.has(edge.source) || !visibleNodeIds.has(edge.target)) {
      return false;
    }

    return true;
  });

  return { nodes: filteredNodes, edges: filteredEdges };
}

/**
 * Returns the set of node IDs that are connected to at least one cross-service edge.
 */
function getCrossServiceNodeIds(edges: GraphEdge[]): Set<string> {
  const nodeIds = new Set<string>();
  for (const edge of edges) {
    if (CROSS_SERVICE_EDGE_TYPES.has(edge.type)) {
      nodeIds.add(edge.source);
      nodeIds.add(edge.target);
    }
  }
  return nodeIds;
}
